import React from 'react';
import { AlertCircle, Check, Circle, Loader2 } from 'lucide-react';
import type { ReputationGeneratedMessage } from '@reach/shared-types/automation-template';
import type { ReputationTemplateTypeEnum } from '@reach/shared-types/business-automation';
import {
  KEPT_ORIGINAL_COPY_NOTE,
  KEPT_ORIGINAL_IMAGE_NOTE,
  MESSAGE_LABEL_BY_TEMPLATE_TYPE,
} from './reputation-defaults.constants';
import styles from './reputation-defaults-dialog.module.css';

const ICON_BY_STATUS = {
  generated: <Check size={14} className={styles.iconDone} />,
  failed: <AlertCircle size={14} className={styles.iconFailed} />,
  unchanged: <Circle size={14} className={styles.iconUnchanged} />,
  pending: <Loader2 size={14} className={styles.iconPending} />,
} satisfies Record<ReputationGeneratedMessage['status'], React.ReactNode>;

interface MessageProgressRowProps {
  message: ReputationGeneratedMessage;
}

const isKnownMessageType = (value: string | undefined): value is ReputationTemplateTypeEnum =>
  value !== undefined && value in MESSAGE_LABEL_BY_TEMPLATE_TYPE;

export const MessageProgressRow: React.FC<MessageProgressRowProps> = ({ message }) => {
  const templateType = message.reputationTemplateType;
  const knownLabel = isKnownMessageType(templateType)
    ? MESSAGE_LABEL_BY_TEMPLATE_TYPE[templateType]
    : undefined;
  const label = knownLabel ?? `Message ${message.actionIndex}`;
  const keptOriginalCopy = message.status === 'failed' || message.status === 'unchanged';
  const copyNote = message.error ?? (keptOriginalCopy ? KEPT_ORIGINAL_COPY_NOTE : undefined);
  const imageNote = message.imageError ? KEPT_ORIGINAL_IMAGE_NOTE : undefined;
  const note = [copyNote, imageNote].filter(Boolean).join(' · ') || undefined;

  return (
    <li className={styles.messageRow}>
      {ICON_BY_STATUS[message.status]}
      <span className={styles.messageLabel}>{label}</span>
      {note && <span className={styles.messageNote}>{note}</span>}
    </li>
  );
};
